import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

const buttonStyles = cva(
  'inline-flex items-center justify-center gap-2 font-semibold select-none transition-[background-color,border-color,color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/35 focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-55',
  {
    variants: {
      variant: {
        primary:   'bg-[var(--color-primary)] text-white shadow-[0_1px_2px_rgba(7,24,46,0.08),0_8px_18px_rgba(79,140,255,0.20)] hover:bg-[var(--color-primary-hover)] disabled:shadow-none',
        secondary: 'bg-violet-tint text-violet shadow-[inset_0_0_0_1px_rgba(79,140,255,0.10)] hover:bg-violet-tint/70',
        danger:    'bg-alert-tint text-alert-text shadow-[inset_0_0_0_1px_rgba(229,72,77,0.10)] hover:bg-alert-tint/80',
        ghost:     'bg-transparent text-body hover:bg-page hover:text-heading',
        link:      'h-auto bg-transparent px-0 text-violet hover:underline',
        outline:   'border border-border bg-card text-body shadow-[0_1px_1px_rgba(7,24,46,0.03)] hover:bg-page hover:text-heading',
      },
      size: {
        sm: 'h-8 px-3 text-sm rounded-input',
        md: 'h-10 px-4 text-sm rounded-input',
        lg: 'h-12 px-5 text-base rounded-input',
        icon: 'h-9 w-9 rounded-input',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonStyles({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {children}
    </button>
  ),
)
Button.displayName = 'Button'
